import { useStoreState } from "ezStore";
import { useApolloClient, useMutation } from "@apollo/client";
import useGroupedTable from "components/customCompenents/table/useGroupedTable";
import { LocationLink, ShipLink } from "utils/LinkUtils";
import { Card } from "@cofano/cck";
import { intl } from "LocaleWrapper";
import { HistoryToggleOff, ViewTimeline } from "@mui/icons-material";
import GroupedTable from "components/customCompenents/table/GroupedTable";
import { Box, Tooltip } from "@mui/material";
import { FormattedDate } from "utils/DateUtils";
import { SalesCharter } from "view/charters/types/charter";
import { DemurrageDocumentsVoyageQuery } from "__generated__/graphql";
import { DataFormat } from "utils";
import EditIcon from "@mui/icons-material/Edit";
import { useCallback, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import SaveIcon from "@mui/icons-material/Save";
import BlockIcon from "@mui/icons-material/Block";
import DatePicker from "components/cck/DatePicker";
import { GetStatusChangesWithEntityDocument, GetStatusChangesWithEntityQuery } from "__generated__/view/statuses/graphql/queries.generated";
import { ChildPropsFromPagination } from "types/commons";
import "../styling/styling.scss";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faShip } from "@fortawesome/pro-solid-svg-icons";
import { UpdateTimesheetDocument } from "__generated__/view/voyages/graphql/voyage.mutations.generated";

type DemurrageTimesheetProps = {
	charter: SalesCharter;
	voyage: NonNullable<DemurrageDocumentsVoyageQuery["voyage"]>;
	refetch: () => void;
	refetchCalls: () => Promise<unknown>;
	refetchSuggestion: () => Promise<unknown>;
}

type StatusChangeFromQuery = ChildPropsFromPagination<GetStatusChangesWithEntityQuery, "statusChanges">
type CallFromQuery = NonNullable<StatusChangeFromQuery["cargo"]>["actions"][number]["call"];

type TimesheetRow = {
	id: number;
	formKey: string;
	statusChange: StatusChangeFromQuery;
	charterNumber: string;
	cargos: string[];
	cargoIds: number[];
	statusId: number;
}

type TimesheetForm = {
	[statusChangeId: string]: number | undefined;
}

const DemurrageTimesheet = ({ charter, voyage, refetch, refetchCalls, refetchSuggestion }: DemurrageTimesheetProps) => {
	const session = useStoreState(state => state.session);
	const client = useApolloClient();

	const [ updateTimesheet, { loading } ] = useMutation(UpdateTimesheetDocument);

	const [ cargoGroups, setCargoGroups ] = useState<{ id: number; rows: TimesheetRow[] }[]>([]);
	const [ editMode, setEditMode ] = useState(false);

	const { control, handleSubmit, reset } = useForm<TimesheetForm>({
		defaultValues: {},
	});

	const { tableProps, refetch: refetchTable } = useGroupedTable({
		id: "demurrageTimesheet",
		paginationEnabled: false,
		headerInFront: false,
		size: "small",
		spacingBetweenGroups: 0,
		onChange: async(_pagination, _filters) => {
			const result = await client.query({
				query: GetStatusChangesWithEntityDocument,
				variables: {
					search: {
						voyageIds: [ voyage.id ],
						cargoIds: charter.cargos?.filter(x => x.actions?.some(y => y.includedForLaytimeCalculation)).map(x => x.id),
					},
				},
			});

			const groupedStatusChanges = (result.data.statusChanges?.content ?? [])
				.filter(s => s.status.laytimeStartStatus || s.status.laytimeEndStatus)
				.reduce(
					(acc, statusChange) => {
						const actions = statusChange.cargo?.actions?.filter(x => x.type === statusChange.status.cargoActionType) ?? [];
						actions.forEach(action => {
							const call = action?.call;

							if (call == null) {
								return;
							}

							const key = call.id;

							acc[key] ??= {
								call: call as NonNullable<CallFromQuery>,
								statusChanges: [] as StatusChangeFromQuery[],
							};

							acc[key].statusChanges.push(statusChange as StatusChangeFromQuery);
						});
						return acc;
					},
					{} as Record<number, { call: NonNullable<CallFromQuery>; statusChanges: StatusChangeFromQuery[] }>,
				);

			const groups = Object.values(groupedStatusChanges).sort(({ call: callA }, { call: callB }) => ((callA.pta ?? 0) - (callB.pta ?? 0))).map(({ call, statusChanges }) => {
				const pickLatest = (a: StatusChangeFromQuery, b: StatusChangeFromQuery) => {
					const at = a.actualTime ?? null;
					const bt = b.actualTime ?? null;

					if (at == null && bt != null) {
						return b;
					}
					if (at != null && bt == null) {
						return a;
					}

					if (at != null && bt != null && at !== bt) {
						return at > bt ? a : b; // keep latest
					}

					return (a.id ?? 0) >= (b.id ?? 0) ? a : b;
				};

				type CargoStatusKey = string;
				const latestPerCargoStatus: Record<CargoStatusKey, StatusChangeFromQuery> = {};

				statusChanges
					.sort((a, b) => (a.status.statusOrder ?? 0) - (b.status.statusOrder ?? 0))
					.forEach((sc) => {
						const cargoId = sc.cargo?.id;
						const statusId = sc.status?.id;
						const charterNumber = sc.cargo?.charter?.charterNumber ?? charter.charterNumber ?? "";

						if (cargoId == null || statusId == null) {
							return;
						}

						const k = `${cargoId}-${statusId}-${charterNumber}`;
						const existing = latestPerCargoStatus[k];

						latestPerCargoStatus[k] = existing ? pickLatest(existing, sc as StatusChangeFromQuery) : (sc as StatusChangeFromQuery);
					});

				const normalized = Object.values(latestPerCargoStatus);

				type RowKey = string;
				const mergedRows: Record<RowKey, TimesheetRow> = {};

				normalized.forEach((sc) => {
					const charterNumber = sc.cargo?.charter?.charterNumber ?? charter.charterNumber ?? "";
					const statusId = sc.status?.id;
					const cargoId = sc.cargo?.id;
					if (statusId == null || cargoId == null) {
						return;
					}

					const actualTime = sc.actualTime ?? null;

					// ignore the seconds component of the time for grouping
					let timeKey = "null";
					if (actualTime != null) {
						const dateObj = new Date(actualTime);
						dateObj.setSeconds(0, 0);
						timeKey = dateObj.getTime().toString();
					}

					const rowKey =
						actualTime == null
							? `${statusId}-${charterNumber}-null`
							: `${statusId}-${charterNumber}-${timeKey}`;

					const existing = mergedRows[rowKey];

					if (!existing) {
						mergedRows[rowKey] = {
							id: sc.id,
							formKey: "",
							statusChange: sc,
							charterNumber,
							cargos: [],
							cargoIds: [],
							statusId,
						};
					} else {
						existing.statusChange = pickLatest(existing.statusChange, sc);
						existing.id = existing.statusChange.id; // keep row.id consistent
					}

					const cargoNumber = sc.cargo?.cargoNumber;
					if (cargoNumber && !mergedRows[rowKey].cargos.includes(cargoNumber)) {
						mergedRows[rowKey].cargos.push(cargoNumber);
					}
					if (!mergedRows[rowKey].cargoIds.includes(cargoId)) {
						mergedRows[rowKey].cargoIds.push(cargoId);
					}
				});

				const rows = Object.entries(mergedRows).map(([ key, row ]) => ({
					...row,
					formKey: `${call.id}-${key}`, // for keeping date picker values visually separated
				}));

				// sort the final rows, first on actual time, then on status order
				rows.sort(sortRows);

				const connectingShip = charter.cargos
					?.flatMap(cargo => cargo.actions ?? [])
					.find(action =>
						action?.call?.id === call.id &&
						action?.connectingShipPortVisit?.ship,
					)
					?.connectingShipPortVisit?.ship;

				return {
					id: call.id,
					render: () => (
						<div style={{ display: "flex" }}>
							<LocationLink location={call.location} />
							{connectingShip && (
								<div style={{ display: "flex", gap: 10 }}>
									<FontAwesomeIcon icon={faShip} style={{ height: 18, width: 18, color: "gray" }} />
									<ShipLink ship={connectingShip} />
								</div>
							)}
						</div>
					),
					rows: rows,
				};
			});

			// for editing
			const currentCargoGroups = groups.map(x => ({
				id: x.id,
				rows: x.rows,
			}));

			setCargoGroups(currentCargoGroups);

			return {
				groups: groups,
				totalElements: result.data.statusChanges?.totalElements ?? 0,
			};
		},
	});

	const save = async (values: TimesheetForm) => {
		const rows = cargoGroups.flatMap(x => x.rows);

		const groupByCargo: Record<number, { statusId: number; actualTime?: number; edited: boolean; }[]> = {};

		Object.entries(values).forEach(([ formKey, actualTime ]) => {
			const row = rows.find(r => r.formKey === formKey);

			if (row == null) {
				return;
			}

			const originalActualTime = row.statusChange.actualTime ? new Date(row.statusChange.actualTime).getTime() : undefined;
			const edited = actualTime !== originalActualTime;

			row.cargoIds.forEach(cargoId => {
				const values = groupByCargo[cargoId] ?? [];
				const index = values.findIndex(x => x.statusId === row.statusId);

				if (index < 0) {
					values.push({
						statusId: row.statusId,
						actualTime: actualTime,
						edited: edited,
					});
				} else {
					const exists = values[index];

					if (edited && !exists.edited) {
						values[index] = {
							statusId: row.statusId,
							actualTime: actualTime,
							edited: edited,
						};
					}
				}

				groupByCargo[cargoId] = values;
			});
		});

		const mutations = Object.entries(groupByCargo).map(([ cargoId, entries ]) => updateTimesheet({
			variables: {
				cargoId: Number(cargoId),
				entries: entries.map(({ statusId, actualTime }) => ({
					statusId: statusId,
					actualTime: actualTime,
				})),
				asOverrideSkipperTimes: true,
			},
		}));

		if (mutations.length > 0) {
			await Promise.all(mutations);
		}

		setEditMode(false);
		reset({});
		refetch();
		refetchCalls();
		refetchSuggestion();
		refetchTable();
	};

	const onEdit = () => {
		const defaultValues: TimesheetForm = {};

		cargoGroups.forEach(group => {
			group.rows.forEach(row => {
				defaultValues[row.formKey] = row.statusChange.actualTime ? new Date(row.statusChange.actualTime).getTime() : undefined;
			});
		});

		reset(defaultValues);

		setEditMode(true);
	};

	const onCancel = () => {
		setEditMode(false);
		reset({});
	};

	const columns = useMemo(() => [
		{
			id: "charterNumber",
			title: intl.formatMessage({ id: "CHARTER_ORDER_NUMBER" }),
			render: (row: TimesheetRow) => row.charterNumber,
		},
		{
			id: "cargos",
			title: intl.formatMessage({ id: "CARGOS" }),
			render: (row: TimesheetRow) => row.cargos?.join(", "),
		},
		{
			id: "status",
			title: intl.formatMessage({ id: "STATUS" }),
			render: (row: TimesheetRow) => DataFormat.status(row.statusChange.status, session.locale),
		},
		{
			id: "actualTime",
			title: intl.formatMessage({ id: "ACTUAL_TIME" }),
			render: (row: TimesheetRow) => {
				const isOverride = row.statusChange.actualTimeOriginal && row.statusChange.actualTimeOverride && row.statusChange.actualTimeOriginal !== row.statusChange.actualTimeOverride;

				if (!editMode) {
					return (
						<Box sx={{ display: "flex", alignItems: "center" }}>
							{row.statusChange.actualTime ? <FormattedDate date={row.statusChange.actualTime} format="lll" /> : "-"}
							{isOverride && (
								<Tooltip title={<>{intl.formatMessage({ id: "CREW_TIMESTAMP" })}: <FormattedDate date={row.statusChange.actualTimeOriginal} /></>}>
									<HistoryToggleOff fontSize="small" htmlColor="gray" sx={{ marginLeft: 1 }} />
								</Tooltip>
							)}
						</Box>
					);
				}

				return (
					<Controller
						name={row.formKey}
						control={control}
						render={({ field: { onChange, value } }) => (
							<DatePicker
								onChange={onChange}
								value={value ?? undefined}
								showTimeInput
								showDateSelect
								timeIntervals={1}
								placeholderText=" "
							/>
						)} />
				);
			},
		},
	], [ editMode, session.locale, control ]);

	const getEntityId = useCallback((row: TimesheetRow) => row.id, []);

	return (
		<div className={`demurrage-timesheet-table ${editMode ? "edit" : ""}`}>
			<Card
				title={intl.formatMessage({ id: "TIMESHEET" })}
				icon={<ViewTimeline fontSize="small" color="primary" />}
				disablePadding
				actions={[
					{
						icon: <EditIcon fontSize="small" />,
						tooltip: intl.formatMessage({ id: "EDIT" }),
						onClick: onEdit,
						hidden: editMode,
					},
					{
						icon: <SaveIcon fontSize="small" />,
						tooltip: intl.formatMessage({ id: "SAVE" }),
						onClick: handleSubmit(save),
						hidden: !editMode,
						disabled: loading,
					},
					{
						icon: <BlockIcon fontSize="small" />,
						tooltip: intl.formatMessage({ id: "CANCEL" }),
						onClick: onCancel,
						hidden: !editMode,
						disabled: loading,
					},
				]}
			>
				<GroupedTable
					key={editMode ? "edit" : "view"}
					{...tableProps}
					getEntityId={getEntityId}
					columns={columns}
				/>
			</Card>
		</div>
	);
};

const sortRows = (a: TimesheetRow, b: TimesheetRow) => {
	const timeA = a.statusChange.actualTime ? new Date(a.statusChange.actualTime).getTime() : null;
	const timeB = b.statusChange.actualTime ? new Date(b.statusChange.actualTime).getTime() : null;

	// both have actual times
	if (timeA != null && timeB != null) {
		if (timeA !== timeB) {
			return timeA - timeB;
		}
		return (a.statusChange.status.statusOrder ?? 0) - (b.statusChange.status.statusOrder ?? 0);
	}

	// one has actual time
	if (timeA != null && timeB == null) {
		return -1;
	}
	if (timeA == null && timeB != null) {
		return 1;
	}

	// none have actual times
	return (a.statusChange.status.statusOrder ?? 0) - (b.statusChange.status.statusOrder ?? 0);
};

export default DemurrageTimesheet;
